import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { fetchVocabByKanji, fetchProgress, fetchWordProgress } from "@/lib/api";
import { compoundUnlockState } from "@/lib/unlock";
import UnlockBadge from "@/components/UnlockBadge";
import JlptBadge from "@/components/JlptBadge";
import { TEST_IDS } from "@/constants/testIds";

const STATUS_DOT_COLOR = { known: "#6E7A5A", learning: "#D29C4C", new: "#7A746D" };

function KanjiInCompound({ annotation, focusedKanji, onSelectKanji }) {
  const ch = annotation.char;
  const isFocused = ch === focusedKanji;
  const isKanji = annotation.is_kanji;
  const onUsed = annotation.on_used;
  const phonetic = annotation.phonetic;

  if (!isKanji) {
    // hiragana / punctuation
    return (
      <span className="font-serif text-2xl text-sumi leading-tight">{ch}</span>
    );
  }

  const colorClass = isFocused ? "text-shu" : "text-sumi";

  return (
    <button
      data-testid={`focus-compound-kanji-${ch}`}
      onClick={(e) => { e.stopPropagation(); if (!isFocused) onSelectKanji?.(ch); }}
      className="inline-flex flex-col items-center group cursor-pointer leading-none"
      title={onUsed ? `on'yomi: ${onUsed}${phonetic ? ` · phonetic ${phonetic.char}` : ""}` : "kun reading"}
    >
      <span className={`font-serif text-2xl ${colorClass} ${!isFocused ? "group-hover:text-shu" : ""} transition-colors leading-none`}>
        {ch}
      </span>
      {/* tiny annotation: shows phonetic component in red when on-yomi is used */}
      {onUsed && phonetic ? (
        <span className="font-serif text-base text-shu mt-0.5 leading-none px-1 bg-shu/10 rounded-sm" title={`phonetic clue: ${phonetic.char}${phonetic.meaning ? ' (' + phonetic.meaning + ')' : ''}`}>
          {phonetic.char}
        </span>
      ) : onUsed ? (
        <span className="font-sans text-[8px] uppercase tracking-widest text-sumiSoft mt-1 leading-none">
          on
        </span>
      ) : (
        <span className="font-sans text-[8px] uppercase tracking-widest text-sumiSoft/60 mt-1 leading-none">
          kun
        </span>
      )}
    </button>
  );
}

export default function CompoundList({ ch, onSelectKanji }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["vocab", ch],
    queryFn: () => fetchVocabByKanji(ch, 30),
    enabled: !!ch,
  });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });
  const wordProgQ = useQuery({ queryKey: ["word-progress"], queryFn: fetchWordProgress });

  if (q.isLoading) {
    return (
      <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft animate-pulse">
        loading compounds…
      </p>
    );
  }
  const items = q.data?.items || [];
  if (items.length === 0) {
    return (
      <p className="font-sans text-sm text-sumiSoft italic">
        No JLPT compounds found for {ch}.
      </p>
    );
  }

  return (
    <div data-testid="focus-compound-section">
      <div className="flex items-center gap-3 mb-3" data-testid={TEST_IDS.focusUsedIn}>
        <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans">
          appears in {q.data.count} compounds
        </p>
        <span className="hairline h-px flex-1" />
      </div>

      <p className="text-[10px] text-sumiSoft font-sans mb-3 leading-snug">
        <span className="text-shu font-serif">red</span> under a kanji = phonetic component driving its on&apos;yomi in that compound · <span className="text-sumi">tap a card to open the compound</span>
      </p>

      <div className="grid gap-2 sm:grid-cols-2 mb-4">
        {items.map((v, i) => {
          const unlock = compoundUnlockState(
            v.annotations.filter((a) => a.is_kanji).map((a) => a.char),
            progQ.data?.items,
          );
          const wordStatus = wordProgQ.data?.items?.[v.word]?.status;
          const handleClick = (e) => {
            // ignore clicks that originated on an inner kanji button
            if (e.target.closest("button[data-testid^='focus-compound-kanji-']")) return;
            navigate(`/word/${encodeURIComponent(v.word)}`);
          };
          return (
          <motion.div
            key={v.word + i}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.3) }}
            data-testid={TEST_IDS.focusCompound(v.word)}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === "Enter") navigate(`/word/${encodeURIComponent(v.word)}`); }}
            className="bg-washi border border-borderDefault hover:border-shu transition-colors px-3 py-2 flex flex-col gap-1 cursor-pointer relative"
          >
            {wordStatus && (
              <span
                aria-hidden
                className="absolute top-1.5 right-1.5 rounded-full"
                style={{ width: 8, height: 8, background: STATUS_DOT_COLOR[wordStatus] }}
                title={`status: ${wordStatus}`}
              />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-end gap-1">
                {v.annotations.map((a, idx) => (
                  <KanjiInCompound
                    key={`${a.char}-${idx}`}
                    annotation={a}
                    focusedKanji={ch}
                    onSelectKanji={onSelectKanji}
                  />
                ))}
              </div>
              <span className="font-serif text-sm text-sumiSoft ml-auto">{v.reading}</span>
              <JlptBadge level={v.jlpt} jouyou={v.jouyou} size="sm" />
              {unlock.total > 0 && (
                <UnlockBadge state={unlock.state} known={unlock.known} total={unlock.total} variant="chip-sm" />
              )}
            </div>
            <p className="font-sans text-xs text-sumi leading-snug">{v.meaning}</p>
          </motion.div>
          );
        })}
      </div>
      <div className="flex justify-center mb-4">
        <span className="w-px h-6 bg-borderDefault" />
      </div>
    </div>
  );
}
